/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { clearForbidden, markExpired, markForbidden } from '@/auth/accessState';
import { hasNoEdiagAccess } from '@/lib/tokenClaims';

/** Minimal router surface needed to route to the access page without importing the router here. */
export interface AuthNav {
  navigate: (to: string) => void;
  currentLocation: () => { pathname: string; search: string };
}

function urlOf(error: unknown): string {
  const url = (error as { config?: { url?: string } } | null)?.config?.url;
  return typeof url === 'string' && url ? url : 'API';
}

function challengeOf(error: unknown): string | undefined {
  const headers = (error as { response?: { headers?: Record<string, unknown> } } | null)?.response
    ?.headers;
  const value = headers?.['www-authenticate'];
  return typeof value === 'string' ? value : undefined;
}

function navigateOnce(nav: AuthNav, to: string): void {
  const { pathname, search } = nav.currentLocation();
  // Don't re-navigate (and loop) when the access page is already at this exact target.
  if (`${pathname}${search}` === to) return;
  nav.navigate(to);
}

/** Central handler for API auth failures: 401 re-authenticates; 403 fails soft unless the token lacks any eDiag role. */
export function handleApiAuthError(status: number, error: unknown, nav: AuthNav): void {
  const url = urlOf(error);
  if (status === 403) {
    const challenge = challengeOf(error);
    console.warn(`[auth] 403 forbidden on ${url}${challenge ? ` - ${challenge}` : ''}`);
    if (hasNoEdiagAccess()) {
      markForbidden();
      navigateOnce(nav, '/unauthorized?reason=forbidden');
    }
    return;
  }
  // Clear stale forbidden verdict so re-auth isn't masked by a prior 403.
  console.warn(`[auth] 401 unauthorized on ${url} - session expired`);
  clearForbidden();
  // Gate every protected route until re-auth; MSAL still holds a client token post-expiry.
  markExpired();
  navigateOnce(nav, '/unauthorized?reason=expired');
}
